import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Flex, FlexProps, HStack, Skeleton, Tag, TagLabel, Text, Tooltip, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import { AlertTriangle, Globe2 } from "lucide-react";
import React, { useMemo } from "react";
import { useAgents } from "../hooks/agents/useAgents";
import { useLanguage } from "../hooks/utility/useLanguage";

type ChatHeaderProps = FlexProps

const ChatHeader: React.FC<ChatHeaderProps> = ({
  ...props
}) => {
  const { currentAgent } = useAgents()
  const { t } = useLanguage()

  // Color mode values
  const headerBg = useColorModeValue('white', 'gray.800')
  const headerBorderColor = useColorModeValue('gray.100', 'gray.600')
  const headerTextColor = useColorModeValue('gray.800', 'white')
  const headerHoverColor = useColorModeValue('gray.600', 'gray.300')
  const warningBg = useColorModeValue('yellow.50', 'yellow.900')
  const warningBorderColor = useColorModeValue('yellow.100', 'yellow.700')
  const warningTextColor = useColorModeValue('yellow.800', 'yellow.200')
  const modalBg = useColorModeValue('white', 'gray.800')

  const isInternetSearchEnabled = useMemo(() => {
    return Boolean(currentAgent?.tools?.includes('INTERNET_SEARCH'))
  }, [currentAgent?.tools])

  // Show a one-time modal/alert the first time an internet-search-enabled agent is active
  const { isOpen, onOpen, onClose } = useDisclosure()

  const acknowledgeWarning = () => {
    localStorage.setItem('internet_search_warning_ack', 'true')
    onClose()
  }

  return (
    <Box {...props} flex="0 0 auto" position="relative">
      <Flex
        as="header"
        h="60px"
        align="center"
        justify="center"
        bg={headerBg}
        px={6}
        py={3}
        position="relative"
        overflow="hidden"
        borderBottom="1px"
        borderColor={headerBorderColor}
      >
      {!currentAgent ? (
        <Skeleton height="20px" width="140px" borderRadius="md" />
      ) : (
        <Tooltip
          label={currentAgent?.description}
          hasArrow
          placement="bottom"
          isDisabled={!currentAgent?.description}
          bg="gray.700"
          color="white"
          fontSize="sm"
          maxW="300px"
          textAlign="center"
        >
          <Text
            fontSize="lg"
            fontWeight="semibold"
            color={headerTextColor}
            cursor={currentAgent?.description ? "help" : "default"}
            _hover={currentAgent?.description ? { color: headerHoverColor } : {}}
            transition="color 0.2s"
          >
            {currentAgent?.name ?? "Unknown Agent"}
          </Text>
        </Tooltip>
      )}

      {/* Right-side badges/actions */}
      {isInternetSearchEnabled && (
        <HStack position="absolute" right={4} top={3} spacing={2}>
          <Tooltip label={t('internetSearch.badgeTooltip')} hasArrow placement="bottom">
            <Tag size="md" variant="subtle" colorScheme="yellow" cursor="pointer" onClick={onOpen}>
              <Globe2 size={16} style={{ marginRight: 6 }} />
              <TagLabel>{t('internetSearch.badgeLabel')}</TagLabel>
            </Tag>
          </Tooltip>
        </HStack>
      )}
      </Flex>

      {/* Inline warning bar below header (always visible when internet search is enabled) */}
      {isInternetSearchEnabled && (
        <Box
          px={6}
          py={2}
          bg={warningBg}
          borderBottom="1px solid"
          borderColor={warningBorderColor}
        >
          <HStack spacing={2} align="center" justify="center">
            <AlertTriangle size={14} color="#B7791F" />
            <Text fontSize="xs" color={warningTextColor} textAlign="center">
              {t('internetSearch.inlineWarning')}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Modal-styled alert for the first-time acknowledgment */}
      {isInternetSearchEnabled && isOpen && (
        <Box position="fixed" inset={0} bg="blackAlpha.400" zIndex={1400} display="flex" alignItems="center" justifyContent="center">
          <Box bg={modalBg} borderRadius="lg" p={6} maxW="520px" w="90%" boxShadow="lg">
            <Alert status="warning" variant="left-accent" mb={4} alignItems="flex-start">
              <AlertIcon />
              <Box>
                <AlertTitle>{t('internetSearch.modalTitle')}</AlertTitle>
                <AlertDescription>{t('internetSearch.modalBody')}</AlertDescription>
              </Box>
            </Alert>
            <HStack justify="flex-end" spacing={3}>
              <Button onClick={acknowledgeWarning} colorScheme="yellow">
                {t('internetSearch.gotIt')}
              </Button>
            </HStack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ChatHeader);
